import { env } from '@/config/env'

/**
 * Carrying a session across the one boundary the product deliberately draws.
 *
 * A dedicated space keeps a host-only cookie: its session has no business being readable by the
 * platform or by the space next door. Widening the cookie to `*.communo.app` would have spared
 * one login and sold back the isolation the whole feature is bought for.
 *
 * So nothing is shared. At the moment someone deliberately crosses, the platform parks the
 * refresh token the browser already holds behind an opaque key that lives sixty seconds and
 * dies on first read; the space redeems it and builds its own host-only session from there.
 * Only the key travels in the URL, and it is worthless anywhere but on the host it names.
 *
 * A customer-owned domain (`partage.ma-ville.fr`) is a different registrable domain and gets a
 * login, as it should: there is no boundary to hand across, only a different site.
 */
const HANDOFF_PARAM = 'handoff'

/** Mints a key for `host`. Returns null when anything at all goes wrong — a failed hand-off
 *  costs a login, never the crossing itself. */
export const mintHandoffKey = async (
  host: string,
  refreshToken: string,
  accessToken: string
): Promise<string | null> => {
  try {
    const response = await fetch(`${env.API_URL}/session/handoff`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ host, refreshToken }),
    })

    if (!response.ok) {
      return null
    }

    const json = (await response.json()) as { key?: string }

    return json.key ?? null
  } catch {
    return null
  }
}

/** The destination, carrying the key. */
export const handoffUrl = (baseUrl: string, key: string | null): string => {
  if (!key) {
    return baseUrl
  }

  const url = new URL(baseUrl)
  url.searchParams.set(HANDOFF_PARAM, key)

  return url.toString()
}

/** The key this page was opened with, if any. Read once. */
export const readHandoffKey = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  return new URLSearchParams(window.location.search).get(HANDOFF_PARAM)
}

/**
 * Takes the key out of the address bar.
 *
 * Called as soon as it has been read, whether or not the redemption then succeeds: a spent key
 * is worthless, but leaving it in the URL puts it in the history, in the referer of the next
 * outbound link, and in whatever the person pastes into a chat to say "look at my new space".
 */
export const forgetHandoffKey = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  const url = new URL(window.location.href)
  url.searchParams.delete(HANDOFF_PARAM)
  window.history.replaceState({}, '', url.toString())
}

/**
 * Redeems the key this page was opened with, and establishes the session from it.
 *
 * Returns true when a session was built. Everything about it fails soft: a spent key, an
 * expired one, a key meant for another host — all end in a login screen, which is exactly
 * where someone would have been without any of this.
 */
export const consumeHandoffOnArrival = async (
  storeSession: (refreshToken: string) => Promise<boolean>
): Promise<boolean> => {
  const key = readHandoffKey()

  if (!key) {
    return false
  }

  // Out of the address bar before anything else, whether or not the rest succeeds: a spent key
  // is worthless, but one left in the URL lands in the history, in the referer of the next
  // outbound link, and in whatever gets pasted into a chat to say "look at my new space".
  forgetHandoffKey()

  const refreshToken = await redeemHandoffKey(key)

  return refreshToken ? storeSession(refreshToken) : false
}

/** Redeems a key for the refresh token it was parked against. */
export const redeemHandoffKey = async (key: string): Promise<string | null> => {
  try {
    const response = await fetch(`${env.API_URL}/session/handoff/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key }),
    })

    if (!response.ok) {
      return null
    }

    const json = (await response.json()) as { refreshToken?: string }

    return json.refreshToken ?? null
  } catch {
    return null
  }
}
